import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, Trash2, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPurge = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('ready'); // ready, confirming, purging, done
  const [result, setResult] = useState(null);

  const handlePurge = async () => {
    if (status === 'ready') {
      setStatus('confirming');
      return;
    }
    
    if (status === 'confirming') {
      setStatus('purging');
      try {
        const res = await axios.delete(`${API_URL}/api/admin/purge-test-users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResult(res.data);
        setStatus('done');
        toast.success('Purge complete!');
      } catch (error) {
        console.error('Purge error:', error);
        toast.error(error.response?.data?.detail || 'Purge failed');
        setStatus('ready');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-6">
          {status === 'done' ? (
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
              <h2 className="text-xl font-bold">Purge Complete</h2>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Deleted <strong>{result?.deleted_users}</strong> users</p>
                <p>Deleted <strong>{result?.deleted_records}</strong> records</p>
              </div>
              <Button onClick={() => navigate('/app/admin')} className="w-full">
                Go to Admin Dashboard
              </Button>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <AlertTriangle className="w-16 h-16 mx-auto text-red-500" />
              <h2 className="text-xl font-bold text-red-600">Delete All Test Users</h2>
              <p className="text-sm text-muted-foreground">
                This will permanently delete <strong>all users except admin accounts</strong> and all their associated data.
              </p>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium">
                  This action cannot be undone!
                </p>
              </div>
              
              {status === 'confirming' && (
                <p className="text-sm font-medium text-red-600">
                  Click again to confirm deletion
                </p>
              )}
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/app/admin')}
                  className="flex-1"
                  disabled={status === 'purging'}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handlePurge}
                  disabled={status === 'purging'}
                  className="flex-1"
                >
                  {status === 'purging' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : status === 'confirming' ? (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Confirm Delete
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All Test Users
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPurge;
